import { useQuery, useMutation} from '@tanstack/react-query'
import { getAllBusinesses, submitBusinessDetailsForm, deleteBusinessDetails, getBusinessDetails, submitFocusGroupForm, submitFocusGroupSessionResponse, getFocusGroupData, getPastSessionResponse, getFocusGroupSessions } from '../api/crud'
import { generateAIInsight } from '../api/ai_crud'
import { FocusGroupFormData, FocusGroupSessionResponseSchema, MyBusinessSchema, QuestionType } from "../data/types";
import { useMemo } from 'react';
import moment from 'moment-timezone';
import {
    KitchenIcon,
    TrainingIcon,
    DigitalIcon,
    CommunityIcon,
    DreamIcon,
    SupplyIcon,
    FeedbackIcon,
    BusinessOperationsIcon,
    GrowthScalingIcon,
    FundingIcon,
    RegulatoryIcon
  } from '../icons/FocusGroupIcons';



  const iconMap: Record<string, React.ComponentType> = {
    'KitchenIcon': KitchenIcon,
    'TrainingIcon': TrainingIcon,
    'DigitalIcon': DigitalIcon,
    'CommunityIcon': CommunityIcon,
    'DreamIcon': DreamIcon,
    'SupplyIcon': SupplyIcon,
    'FeedbackIcon': FeedbackIcon,
    'BusinessOperationsIcon': BusinessOperationsIcon,
    'GrowthScalingIcon': GrowthScalingIcon,
    'FundingIcon': FundingIcon,
    'RegulatoryIcon': RegulatoryIcon
  };


const staleTime = 1000
const isEnabled = true


export const useAllBusinesses = (username: string) => {
    const {data: businesses, refetch: reloadAllBusinesses, isLoading: isLoadingAllBusinesses} = useQuery({ 
        queryKey: ['businesses', username], 
        queryFn: async () => {
            console.log("fetching businesses")
            const res = await getAllBusinesses(username)
            return res ? res : []
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingAllBusinesses, businesses, reloadAllBusinesses}
}

export const useBusinessDetails = (id: string) => {
    const {data: businessDetails, refetch: reloadBusinessDetails, isLoading: isLoadingBusinessDetails} = useQuery({ 
        queryKey: ['businessDetails', id], 
        queryFn: async () => {
            console.log("fetching business details")
            const res = await getBusinessDetails(id)
            return res ? res : {}
        },
        staleTime,
        enabled: isEnabled
    })
    return { isLoadingBusinessDetails, businessDetails, reloadBusinessDetails}
}


export const useUpdateBusinessDetails = (username: string) => {
    const {reloadAllBusinesses} = useAllBusinesses(username)
    
    const addBusinessDetailsMutation = useMutation({ 
        mutationFn: async (businessDetails: MyBusinessSchema ) => {
            console.log("add business details")
            const res = await submitBusinessDetailsForm(businessDetails)
            console.log("submitBusinessDetailsForm res: ", res)
            const {id} = res as MyBusinessSchema
            await reloadAllBusinesses()
            return id
        },
    })

    const addBusinessDetails = (businessDetails: MyBusinessSchema) => {
        addBusinessDetailsMutation.mutate(businessDetails)
        return addBusinessDetailsMutation.data
    }

    const deleteBusinessDetailsMutation = useMutation({ 
        mutationFn: async (businessDetailsId: string) => {
            console.log("delete business details")
            const res = await deleteBusinessDetails(username, businessDetailsId)
            await reloadAllBusinesses()
            return res
        },
    })

    const removeBusinessDetails = (businessDetailsId: string) => {
        deleteBusinessDetailsMutation.mutate(businessDetailsId)
        return deleteBusinessDetailsMutation?.data ? deleteBusinessDetailsMutation.data : ''
    }

    const generateBusinessInsightMutation = useMutation({
        mutationFn: async ({ username, prompt }: { username: string; prompt: string }) => {
            console.log("generate ai business insight")
            return await generateAIInsight(username, prompt);
        },
        // gcTime: 1000 * 10 * 1, // 30 seconds
        // gcTime: 1000 * 60 * 5, // 5 minutes
    })

    const generateBusinessInsight = async (username: string, prompt: string) => {
        const result = await generateBusinessInsightMutation.mutateAsync({ username, prompt });
        return result?.insights || '';
    }

    // const useGenerateAIInsight = (username: string, prompt: string) => {
    //     const { data: aiInsight, refetch: reloadAIInsight, isLoading, isError, error } = useQuery({
    //         queryKey: ['aiInsight', username, prompt],
    //         queryFn: async () => {
    //             return await generateAIInsight(username, prompt);
    //         },
    //         enabled: !!prompt,
    //         staleTime: 5 * 60 * 1000, // 5 minutes
    //     });
    
    //     return {aiInsight, isLoading, isError, error, reloadAIInsight}
    // }

    const isLoadingSetupIntent = addBusinessDetailsMutation.isPending || deleteBusinessDetailsMutation.isPending
    return { addBusinessDetails, removeBusinessDetails, isLoadingSetupIntent, generateBusinessInsight }
}


export const useFocusGroupRegistration = (communityId: string) => {
    const mutation = useMutation({
        mutationFn: async (formData: FocusGroupFormData) => {
            return await submitFocusGroupForm(formData, communityId);
        }
    });

    const submitFocusGroupRegistration = async (formData: FocusGroupFormData) => {
        try {
            const result = await mutation.mutateAsync(formData);
            return result !== null;
        } catch (error) {
            console.error('Focus group submission error:', error);
            return false;
        }
    };

    return {
        submitFocusGroupRegistration,
        isLoading: mutation.isPending,
        isSuccess: mutation.isSuccess,
        isError: mutation.isError,
        error: mutation.error
    };
};


export const useFocusGroupRegistrationData = (uid: string, communityId: string) => {
    const { data: focusGroupData, isLoading, isError, error } = useQuery({
        queryKey: ['focusGroup', uid, communityId],
        queryFn: async () => {
            return await getFocusGroupData(uid, communityId);
        },
        enabled: !!uid,
        staleTime: 5 * 60 * 1000, // 5 minutes
    });

    return {
        focusGroupData: focusGroupData as FocusGroupFormData,
        isLoading,
        isError,
        error
    };
};


export const useFocusGroupSession = (uid: string, sessionId: string) => {
    const { data: pastSessionResponse, isLoading: isLoadingPastSessionResponse, isError: isErrorPastSessionResponse, error: errorPastSessionResponse, refetch: reloadPastSessionResponse } = useQuery({
        queryKey: ['pastSessionResponse', uid, sessionId],
        queryFn: async () => {
            return await getPastSessionResponse(uid, sessionId);
        },
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
    const submitFocusGroupSessionMutation = useMutation({
        mutationFn: async (responses: FocusGroupSessionResponseSchema) => {
            const response = await submitFocusGroupSessionResponse(uid, sessionId as string, responses);
            return response;
        },
        onSuccess: () => {
            reloadPastSessionResponse()
        },
    });

    const submitFocusGroupSession = async (responses: FocusGroupSessionResponseSchema) => {
        try {
            const result = await submitFocusGroupSessionMutation.mutateAsync(responses);
            return result ? result : null;
        } catch (error) {
            console.error('Focus group session submission error:', error);
            return null;
        }
    };

    const isLoading = isLoadingPastSessionResponse || submitFocusGroupSessionMutation.isPending
    const isSuccess = submitFocusGroupSessionMutation.isSuccess
    const isError = isErrorPastSessionResponse ||   submitFocusGroupSessionMutation.isError
    const error = errorPastSessionResponse || submitFocusGroupSessionMutation.error
    const isSubmitting = submitFocusGroupSessionMutation.isPending

    return {
        submitFocusGroupSession,
        pastSessionResponse,
        isLoading,
        isSuccess,
        isError,
        error,
        isSubmitting
    };
};


export const useFocusGroupCommunitySessions = (communityId?: string) => {
    const {
        data: focusGroupSessions,
        isLoading: isLoadingFocusGroupSessions,
        isError: isErrorFocusGroupSessions,
        error: errorFocusGroupSessions
    } = useQuery({
        queryKey: ['focusGroupSessions', communityId],
        queryFn: async () => {
            const response = await getFocusGroupSessions();
            return response ? response : []
        }
    });

    return {
        focusGroupSessions,
        isLoadingFocusGroupSessions,
        isErrorFocusGroupSessions,
        errorFocusGroupSessions
    };  
};


export const useFocusGroupFormattedSessions = (communityId?: string) => {
    const { focusGroupSessions } = useFocusGroupCommunitySessions(communityId);
    const formattedFocusGroupSessions = useMemo(() => 
        focusGroupSessions ? focusGroupSessions.map(session => ({
            ...session,
            sessionDate: moment(session.sessionDate),
            sections: session.sections.map(section => ({
              ...section,
              icon: iconMap[section.icon],
              questions: section.questions.map(question => ({
                ...question,
                type: question.type as QuestionType
              }))
            }))
          })) : [],
        [focusGroupSessions]
    );
    return {formattedFocusGroupSessions};
}
